import { useState } from "react";
import { Eye, EyeOff, KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { errMsg, http } from "@/lib/api";
import { fieldCls, labelCls } from "@/components/admin/BrandFormDialog";

const Field = ({ id, label, value, onChange, show, testId, autoComplete }) => (
  <div>
    <label className={labelCls} htmlFor={id}>{label}</label>
    <input id={id} type={show ? "text" : "password"} required value={value} onChange={(e) => onChange(e.target.value)} autoComplete={autoComplete} data-testid={testId} className={fieldCls} />
  </div>
);

export function ChangePassword() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const strong = form.next.length >= 8 && /[A-Za-z]/.test(form.next) && /\d/.test(form.next);
  const match = form.next && form.next === form.confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!strong) { toast.error("Use at least 8 characters with letters and numbers"); return; }
    if (!match) { toast.error("The new passwords don't match"); return; }
    setBusy(true);
    try {
      const { data } = await http.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      if (data?.token) localStorage.setItem("ph_token", data.token);
      setForm({ current: "", next: "", confirm: "" });
      toast.success("Password changed", { description: "All other signed-in sessions have been logged out." });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl rounded-3xl border border-ph-line bg-ph-surface p-6 md:p-8" data-testid="change-password-panel">
      <p className="flex items-center gap-2 font-display text-2xl font-extrabold text-ph-ink"><KeyRound className="h-5 w-5 text-ph-accent" /> Change password</p>
      <p className="mt-1 text-sm text-ph-muted">After changing it, every other device signed in as admin is logged out.</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <Field id="pw-current" label="Current password" value={form.current} onChange={set("current")} show={show} testId="change-password-current" autoComplete="current-password" />
        <Field id="pw-new" label="New password" value={form.next} onChange={set("next")} show={show} testId="change-password-new" autoComplete="new-password" />
        <p className={`text-xs ${form.next ? (strong ? "text-emerald-700" : "text-amber-700") : "text-ph-muted"}`} data-testid="change-password-hint">
          At least 8 characters, including letters and numbers.
        </p>
        <Field id="pw-confirm" label="Confirm new password" value={form.confirm} onChange={set("confirm")} show={show} testId="change-password-confirm" autoComplete="new-password" />
        {form.confirm && !match && <p className="text-xs text-red-600" data-testid="change-password-mismatch">Passwords don't match.</p>}
        <button type="button" onClick={() => setShow((s) => !s)} data-testid="change-password-toggle-show" className="inline-flex items-center gap-1.5 text-xs font-semibold text-ph-muted hover:text-ph-ink">
          {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} {show ? "Hide" : "Show"} passwords
        </button>
        <button type="submit" disabled={busy} data-testid="change-password-submit"
          className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-ph-lime font-semibold text-ph-navy hover:shadow-[0_0_24px_rgba(182,255,92,0.45)] disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Update password
        </button>
      </form>
    </div>
  );
}
